/**
 * ApiHealthPill (STORY-008-006). Polls GET /health every 60s; green dot on a
 * 200 {status:"ok"}, red otherwise. Clicking a red pill toasts the error.
 * --g-* tokens only.
 */
import { type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { apiClient } from "@/lib/api-client";

interface HealthResponse { status: string; }

export function ApiHealthPill(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isError } = useQuery<HealthResponse>({
    queryKey: ["admin", "health"],
    queryFn: () => apiClient.get<HealthResponse>("/health"),
    refetchInterval: 60_000,
    retry: false,
    staleTime: 0,
  });
  const ok = !isError && data?.status === "ok";
  return (
    <button
      type="button"
      data-testid="admin-health-pill"
      data-health={ok ? "ok" : "error"}
      onClick={() => { if (!ok) toast.error(t("health.error")); }}
      aria-label={ok ? t("health.ok") : t("health.error")}
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--g-hairline)] px-2 py-1 text-xs text-[var(--g-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      <span aria-hidden className={["size-2 rounded-full", ok ? "bg-[var(--g-success)]" : "bg-[var(--g-error)]"].join(" ")} />
      {t("health.label")}
    </button>
  );
}

export default ApiHealthPill;
